import { AnalyticsResponse } from '../../../shared/interfaces/analytics.interface';
import { formatCurrency, formatNumber, formatPercent, formatTrend } from './report-format';

/** Frase principal del reporte: lo que pasó en el período, en una línea. */
export function buildHeadline(d: AnalyticsResponse): string {
  const { totalRevenue, totalAppointments, paymentsCount } = d.kpis;
  const citas = `${formatNumber(totalAppointments)} ${totalAppointments === 1 ? 'cita' : 'citas'}`;
  const cobrado = `${formatCurrency(totalRevenue)} en ${formatNumber(paymentsCount)} ${paymentsCount === 1 ? 'pago' : 'pagos'}`;
  if (totalRevenue > 0 && totalAppointments > 0) return `Cobraste ${cobrado} y agendaste ${citas}.`;
  if (totalRevenue > 0) return `Cobraste ${cobrado}, sin citas agendadas en estas fechas.`;
  if (totalAppointments > 0) return `Agendaste ${citas}, pero aún no hay pagos registrados.`;
  return 'No hubo citas ni pagos en estas fechas.';
}

export type InsightKind = 'revenue' | 'service' | 'attendance' | 'clients' | 'pending';
export type InsightTone = 'up' | 'down' | 'neutral';

/** Una lectura del período: título corto para escanear, detalle y la frase completa para PDF, Excel y copiar. */
export interface InsightItem {
  kind: InsightKind;
  tone: InsightTone;
  title: string;
  detail: string;
  sentence: string;
}

export function buildInsightItems(d: AnalyticsResponse): InsightItem[] {
  const { kpis, comparison, charts } = d;
  const items: InsightItem[] = [];

  if (comparison.revenueTrend !== null && comparison.revenueTrend !== 0) {
    const up = comparison.revenueTrend > 0;
    items.push({
      kind: 'revenue',
      tone: up ? 'up' : 'down',
      title: `Ingresos ${formatTrend(comparison.revenueTrend)}`,
      detail: `Frente a ${formatCurrency(comparison.previousRevenue)} del período anterior.`,
      sentence: `Los ingresos fueron ${formatPercent(Math.abs(comparison.revenueTrend), 1)} ${up ? 'más' : 'menos'} que en el período anterior (${formatCurrency(comparison.previousRevenue)}).`,
    });
  } else if (comparison.revenueTrend === null && kpis.totalRevenue > 0) {
    items.push({
      kind: 'revenue',
      tone: 'neutral',
      title: 'Ingresos sin comparación',
      detail: 'El período anterior no tuvo pagos registrados.',
      sentence: 'El período anterior no tuvo pagos, así que no hay con qué comparar los ingresos.',
    });
  }

  const topService = charts.topServices[0];
  if (topService && topService.revenue > 0) {
    const citas = `${formatNumber(topService.completedCount)} ${topService.completedCount === 1 ? 'cita completada' : 'citas completadas'}`;
    items.push({
      kind: 'service',
      tone: 'neutral',
      title: `${topService.name} es el que más vende`,
      detail: `${formatCurrency(topService.revenue)} en ${citas}.`,
      sentence: `${topService.name} fue el servicio que más vendió: ${formatCurrency(topService.revenue)} en ${citas}.`,
    });
  }

  if (kpis.attendanceRate !== null) {
    const lost: string[] = [];
    if (kpis.cancelledAppointments) lost.push(`${formatNumber(kpis.cancelledAppointments)} ${kpis.cancelledAppointments === 1 ? 'se canceló' : 'se cancelaron'}`);
    if (kpis.noShowAppointments) lost.push(`${formatNumber(kpis.noShowAppointments)} sin asistencia`);
    const lostText = lost.join(' y ');
    items.push({
      kind: 'attendance',
      tone: kpis.attendanceRate >= 85 ? 'up' : kpis.attendanceRate < 70 ? 'down' : 'neutral',
      title: `${formatPercent(kpis.attendanceRate)} de asistencia`,
      detail: `De las citas que ya pasaron${lostText ? `, ${lostText}` : ''}.`,
      sentence: `Se atendió el ${formatPercent(kpis.attendanceRate)} de las citas que ya pasaron${lostText ? ` (${lostText})` : ''}.`,
    });
  }

  if (kpis.newClients > 0) {
    const antes = comparison.previousNewClients;
    const nuevos = `${formatNumber(kpis.newClients)} ${kpis.newClients === 1 ? 'cliente nuevo' : 'clientes nuevos'}`;
    const volvieron = `volvieron ${formatNumber(kpis.returningClients)} que ya conocías`;
    items.push({
      kind: 'clients',
      tone: antes === 0 ? 'neutral' : kpis.newClients > antes ? 'up' : kpis.newClients < antes ? 'down' : 'neutral',
      title: nuevos,
      detail: antes > 0 ? `${formatNumber(antes)} en el período anterior; ${volvieron}.` : `Además ${volvieron}.`,
      sentence: `Llegaron ${nuevos}${antes > 0 ? ` (${formatNumber(antes)} en el período anterior)` : ''} y ${volvieron}.`,
    });
  }

  if (kpis.upcomingAppointments > 0) {
    const n = kpis.upcomingAppointments;
    items.push({
      kind: 'pending',
      tone: 'neutral',
      title: `${formatNumber(n)} ${n === 1 ? 'cita por atender' : 'citas por atender'}`,
      detail: 'Siguen agendadas, por aprobar o confirmadas.',
      sentence: `${formatNumber(n)} ${n === 1 ? 'cita sigue pendiente' : 'citas siguen pendientes'} o por confirmar.`,
    });
  }
  return items;
}

/** Lecturas en una frase cada una (PDF, Excel, texto copiado). */
export function buildInsights(d: AnalyticsResponse): string[] {
  return buildInsightItems(d).map((i) => i.sentence);
}

/** Resumen en texto plano para copiar y pegar (WhatsApp, correo, notas). */
export function buildSummaryText(d: AnalyticsResponse, businessName: string, rangeLabel: string): string {
  return [`${businessName}, del ${rangeLabel}:`, buildHeadline(d), ...buildInsights(d).map((l) => `- ${l}`)].join('\n');
}
